import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import monitor, {
  AlertData,
  DeviceOrientation,
  EnabledVitalSigns,
  FaceSessionOptions,
  HealthMonitorCodes,
  HealthMonitorSession,
  ImageValidity,
  OfflineMeasurements,
  SessionState,
  SmokingStatus,
  VitalSigns,
  VitalSignsResults,
} from "@biosensesignal/web-sdk";
import { InfoType, InfoData } from "../types";

const useMonitor = (
  video: MutableRefObject<HTMLVideoElement>,
  cameraId: string,
  processingTime: number,
  licenseKey: string,
  productId: string,
  startMeasuring: boolean
) => {
  const [session, setSession] = useState<HealthMonitorSession>();
  const [sessionState, setSessionState] = useState<SessionState>();
  const [isMonitorReady, setIsMonitorReady] = useState<boolean>();
  const [enabledVitalSigns, setEnabledVitalSigns] =
    useState<EnabledVitalSigns>();
  const [offlineMeasurements, setOfflineMeasurements] =
    useState<OfflineMeasurements>();
  const [vitalSigns, setVitalSigns] = useState<VitalSigns | null>();

  const [error, setError] = useState<AlertData>({ code: -1 });
  const [warning, setWarning] = useState<AlertData>({ code: -1 });
  const [info, setInfo] = useState<InfoData>({ type: InfoType.NONE });
  const isDismissing = useRef<boolean>(false);
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const [finalResults, setFinalResults] = useState<VitalSignsResults | null>(
    null
  ); // State to store final results
  const userName =
    JSON.parse(localStorage.getItem("userBMI") || "{}").name || "Unknown";

  const setInfoWithDismiss = useCallback(
    (info: InfoData, seconds?: number) => {
      if (!isDismissing.current) {
        setInfo(info);
        if (seconds) {
          isDismissing.current = true;
          setTimeout(() => {
            setInfo({ type: InfoType.NONE });
            isDismissing.current = false;
          }, seconds * 1000);
        }
      }
    },
    [InfoType, setInfo, info, isDismissing, isDismissing.current]
  );

  const updateVitalSigns = useCallback((vitalSigns) => {
    setVitalSigns((prev) => ({
      ...prev,
      ...vitalSigns,
    }));
  }, []);

  const onVitalSign = useCallback((vitalSign: VitalSigns) => {
    updateVitalSigns(vitalSign);
  }, []);

  const onFinalResults = useCallback((vitalSignsResults: VitalSignsResults) => {
    setVitalSigns(null);
    console.log("vitalSignsResults.results final", vitalSignsResults.results);
    updateVitalSigns(vitalSignsResults.results);
    setShowModal(true); // Open modal
  }, []);

  const onError = (errorData: AlertData) => {
    setError(errorData);
  };

  const onWarning = (warningData: AlertData) => {
    if (
      warningData.code ===
      HealthMonitorCodes.MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING
    ) {
      setVitalSigns(null);
    }
    setWarning(warningData);
  };

  const onStateChange = useCallback((state: SessionState) => {
    setSessionState(state);
    if (state === SessionState.MEASURING) {
      setVitalSigns(null);
    }
  }, []);

  const onEnabledVitalSigns = useCallback((vitalSigns: EnabledVitalSigns) => {
    setEnabledVitalSigns(vitalSigns);
  }, []);

  const onOfflineMeasurement = useCallback(
    (offlineMeasurements: OfflineMeasurements) => {
      setOfflineMeasurements(offlineMeasurements);
    },
    []
  );

  const onActivation = useCallback((activationId: string) => {
    // the device has been activated with activationId
  }, []);

  const onImageData = useCallback((imageValidity: ImageValidity) => {
    let message: string;
    if (imageValidity != ImageValidity.VALID) {
      switch (imageValidity) {
        case ImageValidity.INVALID_DEVICE_ORIENTATION:
          message = "Unsupported Orientation";
          break;
        case ImageValidity.TILTED_HEAD:
          message = "Head Tilted";
          break;
        case ImageValidity.FACE_TOO_FAR: // Placeholder, currently not supported
          message = "You Are Too Far";
          break;
        case ImageValidity.UNEVEN_LIGHT:
          message = "Uneven Lighting";
          break;
        case ImageValidity.INVALID_ROI:
        default:
          message = "Face Not Detected";
      }
      setInfo({
        type: InfoType.INSTRUCTION,
        message: message,
      });
    } else {
      setInfoWithDismiss({ type: InfoType.NONE });
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await monitor.initialize({
          licenseKey: "F84045-BD6B05-4C0989-4065D6-E3088C-89E163",
          licenseInfo: {
            onEnabledVitalSigns,
            onOfflineMeasurement,
            onActivation,
          },
        });
       
        setIsMonitorReady(true);
        setError({ code: -1 });
      } catch (e) {
        console.error("Error initializing HealthMonitor", e);
        setIsMonitorReady(false);
        setError({ code: e.errorCode });
      }
    })();
  }, [licenseKey, productId]);

  useEffect(() => {
    (async () => {
      try {
        if (!isMonitorReady || !processingTime || !video.current) {
          return;
        }

        sessionState === SessionState.ACTIVE && session.terminate();
        const UserBMI = JSON.parse(localStorage.getItem("userBMI"));
        const options: FaceSessionOptions = {
          input: video.current,
          cameraDeviceId: cameraId,
          processingTime,
          onVitalSign,
          onFinalResults,
          onError,
          onWarning,
          onStateChange,
          orientation: DeviceOrientation.PORTRAIT,
          onImageData,
          ...(UserBMI && {
            userInformation: {
              sex: UserBMI.gender,
              age: UserBMI.age,
              weight: UserBMI.weight,
              height: UserBMI.height || 0, // Provide a default value if height is missing
              smokingStatus:
                UserBMI.smoking === true
                  ? SmokingStatus.SMOKER
                  : UserBMI.smoking === false
                  ? SmokingStatus.NON_SMOKER
                  : SmokingStatus.UNSPECIFIED,
            },
          }),
          // isAnalyticsEnabled: true,
          /*******************************************************************************
           * For accurate vital signs calculation the user's parameters should be provided.
           * The following is an example of how the parameters are passed to the SDK:
           *
           * subjectDemographic: {sex: Sex.MALE, age: 35, weight: 75, height: 165, smokingStatus: "SMOKER"}
           *
           * When measuring a new user then a new session must be created using the
           * new user's parameters
           * The parameters are used locally on the device only for the vital sign
           * calculation, and deleted when the session is terminated.
           *******************************************************************************/
        };

        const faceSession = await monitor.createFaceSession(options);
        setSession(faceSession);
        setError({ code: -1 });
      } catch (e) {
        setError({ code: e.errorCode });
        console.error("Error creating a session", e);
      }
    })();
  }, [processingTime, isMonitorReady]);

  useEffect(() => {
    if (startMeasuring) {
      if (sessionState === SessionState.ACTIVE) {
        session.start();
        setError({ code: -1 });
      }
    } else {
      sessionState === SessionState.MEASURING && session.stop();
    }
  }, [startMeasuring]);

  return {
    sessionState,
    vitalSigns: {
      pulseRate: {
        value: vitalSigns?.pulseRate?.value,
        confidenceLevel: vitalSigns?.pulseRate?.confidenceLevel,
      },
      respirationRate: {
        value: vitalSigns?.respirationRate?.value,
        confidenceLevel: vitalSigns?.respirationRate?.confidenceLevel,
      },
      stressLevel: {
        value: vitalSigns?.stressLevel?.value,
      },
      stressIndex: {
        value: vitalSigns?.stressIndex?.value,
      },
      bloodPressure: {
        value: vitalSigns?.bloodPressure?.value,
      },
      hemoglobin: {
        value: vitalSigns?.hemoglobin?.value,
      },
      hemoglobinA1c: {
        value: vitalSigns?.hemoglobinA1c?.value,
      },
      highBloodPressureRisk: {
        value: vitalSigns?.highBloodPressureRisk?.value,
      },
      highFastingGlucoseRisk: {
        value: vitalSigns?.highFastingGlucoseRisk?.value,
      },
      highHemoglobinA1CRisk: {
        value: vitalSigns?.highHemoglobinA1CRisk?.value,
      },
      highTotalCholesterolRisk: {
        value: vitalSigns?.highTotalCholesterolRisk?.value,
      },
      lowHemoglobinRisk: {
        value: vitalSigns?.lowHemoglobinRisk?.value,
      },
      meanRri: {
        value: vitalSigns?.meanRri?.value,
        confidenceLevel: vitalSigns?.meanRri?.confidenceLevel,
      },
      prq: {
        value: vitalSigns?.prq?.value,
        confidenceLevel: vitalSigns?.prq?.confidenceLevel,
      },
      sdnn: {
        value: vitalSigns?.sdnn?.value,
        confidenceLevel: vitalSigns?.sdnn?.confidenceLevel,
      },
      rmssd: {
        value: vitalSigns?.rmssd?.value,
      },
      sd1: {
        value: vitalSigns?.sd1?.value,
      },
      sd2: {
        value: vitalSigns?.sd2?.value,
      },
      pnsIndex: {
        value: vitalSigns?.pnsIndex?.value,
      },
      pnsZone: {
        value: vitalSigns?.pnsZone?.value,
      },
      snsIndex: {
        value: vitalSigns?.snsIndex?.value,
      },
      snsZone: {
        value: vitalSigns?.snsZone?.value,
      },
      wellnessLevel: {
        value: vitalSigns?.wellnessLevel?.value,
      },
      wellnessIndex: {
        value: vitalSigns?.wellnessIndex?.value,
      },
      lfhf: {
        value: vitalSigns?.lfhf?.value,
      },
      ascvdRisk: {
        value: vitalSigns?.ascvdRisk?.value,
      },
      heartAge: {
        value: vitalSigns?.heartAge?.value,
      },
      normalizedStressIndex: {
        value: vitalSigns?.normalizedStressIndex?.value,
      },
    },
    offlineMeasurements,
    error,
    warning,
    info,
    showModal,
  };
};

export default useMonitor;
